import React, { lazy, memo, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer, Slide } from 'react-toastify';
import * as Sentry from '@sentry/react';
import store from './redux/store';
import './libcss';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Loader from './component/Loader';
import ErrorBoundary from './component/ErrorBoundary';
import PanelistPageLoad from './component/PanelistPageLoad';
import CandidateCalendarSync from './container/CandidateCalendarSync';
import InterviewerCalendarSync from './container/InterviewerCalendarSync';
import PanelistSlotView from './container/PanelistSlotView';
import CancelCalendarInterview from './container/CancelCalendarInterview';

const envMode = process.env.REACT_APP_ENV;

const shouldEnableSentry = ['prod', 'test', 'dev', 'dev2'].includes(envMode);

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason) {
    if (!sentryInitialized) return;

    const now = Date.now();

    // Prevent restart spam (5s debounce)
    if (now - lastRestartAt < 5000) return;
    lastRestartAt = now;

    try {
        Sentry.endSession();
        Sentry.startSession();

        Sentry.setContext('session_restart', {
            reason: reason,
            timestamp: new Date().toISOString(),
        });

        Sentry.flush(5000);
        console.log('[Sentry] Session restarted:', reason);
    } catch (e) {
        console.warn('[Sentry] Failed to restart session', e);
    }
}

if (shouldEnableSentry) {
    Sentry.init({
        dsn: 'https://e689391cb7b2956b480284f155b89499@o4506863228616704.ingest.us.sentry.io/4511568342745088',

        release: process.env.REACT_APP_VERSION,
        environment: envMode,

        autoSessionTracking: true,

        ignoreErrors: [
            // Thrown by third-party browser extensions (antivirus/toolbar COM bridges on Windows),
            // never by app code — the JS runtime has no native error with this message shape.
            /Object Not Found Matching Id/i,
        ],

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [
                    /^https:\/\/dev\.api\.incruiter\.com/,
                    /^https:\/\/test\.api\.incruiter\.com/,
                    /^https:\/\/api\.incruiter\.com/,
                    /^https:\/\/.*\.incruiter\.com/,
                ],
                networkRequestHeaders: ['Authorization', 'Content-Type'],
                networkResponseHeaders: ['Content-Type'],
            }),
            Sentry.replayCanvasIntegration(),
        ],

        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,

        tracePropagationTargets: ['localhost', /^https:\/\/.*\.incruiter\.com/],
        enableLogs: true,
    });

    sentryInitialized = true;
    Sentry.setTag('env', envMode);

    window.addEventListener('online', function () {
        restartSentrySession('network_online');
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            restartSentrySession('tab_visible');
        }
    });
}

const CloseButton = ({ closeToast }) => (
    <i className="fa fa-times-circle align-self-center f-18 text-white" onClick={closeToast} />
);
const InterviewerCalendar = lazy(() => import('./container/InterviewerCalendar'));
const CandidateCalendar = lazy(() => import('./container/CandidateCalendar'));
const RescheduleCalendar = lazy(() => import('./container/RescheduleCalendar'));
const NotFound = lazy(() => import('./container/404NotFound'));
const RouteList = memo(() => {
    return (
        <Router>
            <ErrorBoundary>
                <Suspense fallback={<Loader />}>
                    <Routes>
                        <Route exact={true} path={'reschedule'} element={<RescheduleCalendar />}>
                            <Route path={':interviewId'} element={<RescheduleCalendar />}>
                                <Route path={':roomId'} element={<RescheduleCalendar />} />
                                <Route path={':roomId/:tenantId'} element={<RescheduleCalendar />} />
                            </Route>
                        </Route>
                        <Route exact={true} path={'interviewer'} element={<InterviewerCalendar />}>
                            <Route path={':calendarId'} element={<InterviewerCalendar />} />
                            <Route path={':calendarId/:tenantId'} element={<InterviewerCalendar />} />
                        </Route>
                        <Route exact={true} path={'sync-interviewer'} element={<InterviewerCalendarSync />}>
                            <Route path={':calendarId'} element={<InterviewerCalendarSync />} />
                            <Route path={':calendarId/:tenantId'} element={<InterviewerCalendarSync />} />
                        </Route>
                        <Route exact={true} path={'candidate'} element={<CandidateCalendar />}>
                            <Route path={':calendarId'} element={<CandidateCalendar />} />
                            <Route path={':calendarId/:tenantId'} element={<CandidateCalendar />} />
                        </Route>
                        <Route exact={true} path={'sync-candidate'} element={<CandidateCalendarSync />}>
                            <Route path={':calendarId'} element={<CandidateCalendarSync />} />
                            <Route path={':calendarId/:tenantId'} element={<CandidateCalendarSync />} />
                        </Route>
                        <Route exact={true} path={'view-slot'} element={<PanelistSlotView />}>
                            <Route path={':mode/:panelistId'} element={<PanelistSlotView />} />
                            <Route path={':mode/:panelistId/:tenantId'} element={<PanelistSlotView />} />
                        </Route>
                        <Route exact={true} path={'sync-calendar'} element={<PanelistPageLoad />} />
                        <Route exact={true} path={'cancel-interview'} element={<CancelCalendarInterview />}>
                            <Route path={':calendarId'} element={<CancelCalendarInterview />} />
                        </Route>
                        <Route
                            exact={true}
                            path={'reschedule-interview'}
                            element={<CandidateCalendarSync type="reschedule" />}
                        >
                            <Route
                                path={':interviewId/:calendarId'}
                                element={<CandidateCalendarSync type="reschedule" />}
                            />
                            <Route
                                path={':interviewId/:calendarId/:tenantId'}
                                element={<CandidateCalendarSync type="reschedule" />}
                            />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </ErrorBoundary>
        </Router>
    );
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <Provider store={store}>
            <RouteList />
            <ToastContainer
                autoClose={2000}
                draggable={false}
                transition={Slide}
                closeButton={CloseButton}
                hideProgressBar={true}
                toastClassName="toast-notification"
            />
        </Provider>
    </React.StrictMode>
);
